/* @jsxImportSource @emotion/react */

import React, { useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useData } from 'vike-react/useData'

import { ImgSanity } from '@slite/components'

import { FilterBar, FilterPill } from '../../components/FilterBar'
import { Section } from '../../components/Section'
import { SectionLastCTA } from '../../components/SectionLastCTA'
import { SlitePage } from '../../components/SlitePage'
import { mediaQuerySmall } from '../../components/style'
import { WebsiteText } from '../../components/WebsiteText'
import { useRoutes } from '../../routes'
import type { Data } from './+data'
import { CustomerStoryGrid } from './CustomerStoryGrid'
import type { CustomerStoryCardData } from './customerStoryCardFields'
import { INDUSTRY_LABELS } from './industries'

const FILTERED_INDUSTRY_COUNT = 3

const heroIllustrationCss = {
  width: 300,
  [mediaQuerySmall]: { display: 'none' },
} as const

// Most-told industries first; ties keep the order of the newest story.
function pickMostToldIndustries(
  caseStudies: CustomerStoryCardData[],
  count: number
) {
  const storiesPerIndustry = new Map<string, number>()
  for (const industry of caseStudies.flatMap((cs) => cs.industries ?? [])) {
    storiesPerIndustry.set(
      industry,
      (storiesPerIndustry.get(industry) ?? 0) + 1
    )
  }
  return [...storiesPerIndustry.entries()]
    .filter(([industry]) => industry in INDUSTRY_LABELS)
    .sort(([, a], [, b]) => b - a)
    .slice(0, count)
    .map(([industry]) => industry)
}

export default function CustomersIndexPage() {
  const intl = useIntl()
  const routes = useRoutes()
  const { caseStudies } = useData<Data>()
  const [selectedIndustry, setSelectedIndustry] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState('')

  const industryLabel = (industry: string) =>
    intl.formatMessage(INDUSTRY_LABELS[industry])

  const filteredIndustries = pickMostToldIndustries(
    caseStudies,
    FILTERED_INDUSTRY_COUNT
  )
  const searchLabel = intl.formatMessage({
    id: 'website.customers.index.search',
    defaultMessage: 'Search stories',
  })

  const queryWords = searchQuery.trim().toLowerCase().split(/\s+/)
  const visibleCaseStudies = caseStudies.filter((caseStudy) => {
    const industries = caseStudy.industries ?? []
    if (selectedIndustry && !industries.includes(selectedIndustry)) {
      return false
    }
    const haystack = [
      caseStudy.title,
      ...industries.filter((i) => i in INDUSTRY_LABELS).map(industryLabel),
    ]
      .join(' ')
      .toLowerCase()
    return queryWords.every((word) => haystack.includes(word))
  })

  const title = intl.formatMessage({
    id: 'website.customers.page.title',
    defaultMessage: 'Slite - Our Customers',
  })
  const description = intl.formatMessage({
    id: 'website.customers.page.description',
    defaultMessage:
      'Hear from our customers about what makes Slite the better option for their team.',
  })

  return (
    <SlitePage
      canonicalUrl={routes.customers.index()}
      background="moon-dust-lighter"
      footerBackground="moon-dust-lighter"
      title={title}
      ogTitle={title}
      ogDescription={description}
      ogImage="https://cdn.sanity.io/images/dnsgqii5/production/2c0246fe68909709d9ae15080d8a684cf73a3602-1200x630.jpg"
    >
      <Section
        background="moon-dust-lighter"
        containerTopPadding={80}
        containerBottomPadding={60}
      >
        <div
          className="flexbox flexbox-align-center flexbox-space-between"
          css={{ gap: 48 }}
        >
          <div css={{ maxWidth: 640 }}>
            <WebsiteText
              variant="heroTitle"
              className="margin-bottom-24"
              css={{ textWrap: 'balance' }}
            >
              <FormattedMessage
                id="website.customers.index.hero.title"
                defaultMessage="Hear stories from Slite customers"
              />
            </WebsiteText>
            <WebsiteText variant="body-lg" className="shade-05">
              <FormattedMessage
                defaultMessage="Hear how Slite customers have leveled up collaboration and innovation in their organizations during times of immense change."
                id="website.customers.page.index.description"
              />
            </WebsiteText>
          </div>
          <ImgSanity
            src="https://cdn.sanity.io/images/dnsgqii5/production/3efef3bf76eceb4cb01dce219d920a9e6f3f309a-2252x2400.png"
            alt={intl.formatMessage({
              id: 'website.customers.page.heroImage.alt',
              defaultMessage: 'Customers page illustration',
            })}
            className="flexbox-no-shrink"
            css={heroIllustrationCss}
            priority
          />
        </div>
      </Section>

      <Section background="moon-dust-lighter" containerTopPadding={0}>
        <FilterBar
          background="moon-dust-lighter"
          searchQuery={searchQuery}
          onSearchQueryChange={setSearchQuery}
          searchLabel={searchLabel}
          searchPlaceholder={searchLabel}
        >
          <FilterPill
            isActive={selectedIndustry === null}
            onClick={() => setSelectedIndustry(null)}
          >
            <FormattedMessage
              id="website.customers.index.filter.latest"
              defaultMessage="Latest"
            />
          </FilterPill>
          {filteredIndustries.map((industry) => (
            <FilterPill
              key={industry}
              isActive={selectedIndustry === industry}
              onClick={() => setSelectedIndustry(industry)}
            >
              {industryLabel(industry)}
            </FilterPill>
          ))}
        </FilterBar>
        <div className="margin-top-32">
          {visibleCaseStudies.length > 0 ? (
            // Remounting per filter replays the reveal; search doesn't.
            <CustomerStoryGrid
              key={selectedIndustry ?? 'latest'}
              caseStudies={visibleCaseStudies}
              withWideCards
              animateReveal={!searchQuery.trim()}
            />
          ) : (
            <WebsiteText
              variant="body"
              className="shade-20 text-align-center padding-vertical-60"
            >
              <FormattedMessage
                id="website.customers.index.noResults"
                defaultMessage="No customer stories match your search."
              />
            </WebsiteText>
          )}
        </div>
      </Section>

      <SectionLastCTA background="moon-dust-lighter" />
    </SlitePage>
  )
}
